/**
 * runidea.ai — Phase 14 Wave 3 — useFeatureFlag hook.
 *
 * Reads from `/api/feature-flags/public`, caches the response in-memory for
 * 60s so every component-render doesn't refetch. Falls back to `true` (open
 * by default) so a network blip never hides features unintentionally.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

const TTL_MS = 60_000;
let _cache = null;
let _cacheAt = 0;
let _inflight = null;

async function _load() {
  const now = Date.now();
  if (_cache && now - _cacheAt < TTL_MS) return _cache;
  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const { data } = await api.get("/feature-flags/public");
      _cache = data?.flags || {};
      _cacheAt = Date.now();
      return _cache;
    } catch {
      // best-effort — return last cached or empty.
      return _cache || {};
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function clearFeatureFlagCache() {
  _cache = null;
  _cacheAt = 0;
}

export function useFeatureFlag(key, defaultValue = true) {
  const [value, setValue] = useState(() => (_cache ? _cache[key] ?? defaultValue : defaultValue));
  useEffect(() => {
    let alive = true;
    _load().then((flags) => {
      if (alive) setValue(flags[key] ?? defaultValue);
    });
    return () => { alive = false; };
  }, [key, defaultValue]);
  return value;
}

export function useFeatureFlags() {
  const [flags, setFlags] = useState(() => _cache || {});
  useEffect(() => {
    let alive = true;
    _load().then((f) => { if (alive) setFlags(f); });
    return () => { alive = false; };
  }, []);
  return flags;
}
